import { dateInRange } from 'shared';

import { Column, FilterState, Row } from './types';

export function filterRows<T extends Row>({
  filterState,
  rows,
  columns
}: {
  filterState: FilterState[];
  rows: T[];
  columns: Column<T>[];
}) {
  if (filterState.length === 0) {
    return rows;
  }

  const filters = filterState.map(filter => ({ ...filter, substring: filter.substring?.toLowerCase() }));

  // Global text search across all columns that have textSearch enabled
  const globalTextFilter = filters.find(f => f.key === 'textSearch' && f.substring);
  const filtersExcludingGlobal = filters.filter(f => f.key !== 'textSearch');

  // Group filters by key (excluding global text search)
  const filtersByKey = filtersExcludingGlobal.reduce(
    (acc, filter) => {
      if (!acc[filter.key]) {
        acc[filter.key] = [];
      }
      acc[filter.key].push(filter);
      return acc;
    },
    {} as Record<string, typeof filters>
  );

  return rows.filter(row => {
    // Apply global text search if present: match any enabled column
    if (globalTextFilter && globalTextFilter.substring) {
      const substring = globalTextFilter.substring;
      const anyMatch = columns.some(column => {
        if (!column.filters?.textSearch?.enabled) return false;
        const rawValue = row[column.key as keyof T];
        const value = column?.filterValue
          ? column?.filterValue(row)
          : typeof rawValue === 'number'
            ? rawValue.toString()
            : (rawValue as unknown as string);
        const fn = column?.filters?.textSearch?.fn;
        if (fn) {
          return fn(row, substring);
        }
        if (typeof value !== 'string') return false;
        return value.toLowerCase().includes(substring);
      });
      if (!anyMatch) return false;
    }

    // For each column, check if the row matches any of its filters
    return Object.entries(filtersByKey).every(([key, columnFilters]) => {
      const column = columns.find(c => c.key === key);
      if (!column) return true;

      const rawValue = row[key as keyof T];
      const value = column?.filterValue
        ? column?.filterValue(row)
        : typeof rawValue === 'number'
          ? rawValue.toString()
          : rawValue;

      // A row matches if it satisfies ANY of the filters for this column
      return columnFilters.some(
        ({ substring, oneOf, notOneOf, startDate, endDate, onDate, beforeDate, afterDate, minValue, maxValue }) => {
          const fn = column?.filters?.textSearch?.fn;

          if (fn && substring) {
            return fn(row, substring);
          }

          if (substring) {
            if (typeof value !== 'string') return false;
            return value.toLowerCase().includes(substring);
          } else if (oneOf) {
            if (column.filters?.oneOf?.defaultOption.anyFilterOption.value === oneOf[0]) {
              return true;
            }
            if (Array.isArray(value)) {
              return value.some(v => oneOf.includes(v));
            }

            // @ts-ignore
            return oneOf.includes(value);
          } else if (notOneOf) {
            if (value == null) return true;
            if (Array.isArray(value)) {
              return !value.some(v => notOneOf.includes(v as string | number | boolean));
            }
            return !notOneOf.includes(value as string | number | boolean);
          } else if (
            startDate ||
            endDate ||
            onDate !== undefined ||
            beforeDate !== undefined ||
            afterDate !== undefined
          ) {
            const rowDate = value instanceof Date ? value : typeof value === 'string' ? new Date(value) : null;
            if (rowDate === null || isNaN(rowDate.getTime())) return false;
            // afterDate: exclusive (row > selected date) → rangeStart = midnight, dateInRange uses date > start
            // beforeDate: exclusive (row < selected date) → rangeEnd = midnight, dateInRange uses date < end
            // onDate: inclusive (row on that day) → rangeStart = midnight, rangeEnd = end of day
            // range: inclusive both ends → startDate at midnight, endDate at end of day
            const rangeStart =
              startDate ??
              (afterDate ? new Date(afterDate.getFullYear(), afterDate.getMonth(), afterDate.getDate()) : undefined) ??
              (onDate ? new Date(onDate.getFullYear(), onDate.getMonth(), onDate.getDate()) : undefined);
            const rangeEnd =
              endDate !== undefined
                ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999)
                : beforeDate !== undefined
                  ? new Date(beforeDate.getFullYear(), beforeDate.getMonth(), beforeDate.getDate())
                  : onDate !== undefined
                    ? new Date(onDate.getFullYear(), onDate.getMonth(), onDate.getDate(), 23, 59, 59, 999)
                    : undefined;
            return dateInRange(rowDate, rangeStart, rangeEnd);
          } else if (minValue !== undefined || maxValue !== undefined) {
            if (minValue !== undefined && maxValue !== undefined) {
              return Number(value) >= Number(minValue) && Number(value) <= Number(maxValue);
            } else {
              return minValue !== undefined
                ? Number(value) >= Number(minValue)
                : true && maxValue !== undefined
                  ? Number(value) <= Number(maxValue)
                  : true;
            }
          }

          return false;
        }
      );
    });
  });
}
